import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Layout from '../components/Layout';

export default function Dashboard() {
  const { utente } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (utente.ruolo === 'studio') {
      api.get('/statistiche').then(r => setStats(r.data)).catch(() => {});
    } else {
      Promise.all([
        api.get('/lavoratori'),
        api.get('/comunicazioni', { params: { anno: new Date().getFullYear() } }),
      ]).then(([lav, com]) => {
        setStats({
          lavoratori: lav.data.totale,
          comunicazioni: com.data.totale,
          ultimeCom: com.data.comunicazioni.slice(0, 5),
        });
      }).catch(() => {});
    }
  }, [utente.ruolo]);

  return (
    <Layout>
      <div style={styles.welcome}>
        <h2 style={styles.welcomeTitle}>Benvenuto, {utente.nome}</h2>
        <p style={styles.welcomeSub}>
          {utente.ruolo === 'studio'
            ? 'Pannello di gestione Studio Boldrin — Gestione H-R'
            : 'Area riservata — Gestione H-R'}
        </p>
      </div>

      {utente.ruolo === 'studio' ? (
        <StudioDashboard stats={stats} navigate={navigate} />
      ) : (
        <ClienteDashboard stats={stats} navigate={navigate} />
      )}
    </Layout>
  );
}

function StudioDashboard({ stats, navigate }) {
  const cards = [
    { titolo: 'Clienti', desc: 'Gestisci aziende e account', path: '/clienti', count: stats?.totali?.totale_clienti },
    { titolo: 'Lavoratori', desc: 'Anagrafica completa', path: '/lavoratori', count: stats?.totali?.totale_lavoratori },
    { titolo: 'Comunicazioni', desc: 'Invio e archivio', path: '/comunicazioni', count: stats?.totali?.comunicazioni_inviate, label: 'inviate' },
    { titolo: 'Statistiche', desc: 'Report e alert', path: '/statistiche' },
  ];

  return (
    <>
      <div style={styles.grid}>
        {cards.map(c => (
          <div key={c.path} style={styles.card} onClick={() => navigate(c.path)}>
            <div style={styles.cardTop}>
              <h3 style={styles.cardTitle}>{c.titolo}</h3>
              {c.count !== undefined && (
                <div style={styles.cardCount}>
                  <span style={styles.countNum}>{c.count}</span>
                  {c.label && <span style={styles.countLabel}>{c.label}</span>}
                </div>
              )}
            </div>
            <p style={styles.cardDesc}>{c.desc}</p>
          </div>
        ))}
      </div>

      {stats?.clienti_inattivi?.length > 0 && (
        <div style={styles.alertBox}>
          <strong>Attenzione:</strong> {stats.clienti_inattivi.length} client{stats.clienti_inattivi.length === 1 ? 'e' : 'i'} senza comunicazioni da oltre {stats.giorni_alert} giorni
          <button onClick={() => navigate('/statistiche')} style={styles.alertLink}>Vedi dettagli →</button>
        </div>
      )}
    </>
  );
}

function ClienteDashboard({ stats, navigate }) {
  const [statsCliente, setStatsCliente] = useState(null);
  const [meseOffset, setMeseOffset] = useState(0);
  const formatData = (d) => new Date(d).toLocaleDateString('it-IT');

  useEffect(() => {
    api.get('/statistiche/cliente', { params: { mese_offset: meseOffset } })
      .then(r => setStatsCliente(r.data)).catch(() => {});
  }, [meseOffset]);

  const tipi = statsCliente?.tipi || {};
  const totale = parseInt(tipi.totale) || 0;
  const categorie = [
    { label: 'Lavoro a chiamata', valore: parseInt(tipi.chiamata) || 0, colore: '#D1D179' },
    { label: 'Tempo indeterminato', valore: parseInt(tipi.indeterminato) || 0, colore: '#232323' },
    { label: 'Tempo determinato', valore: parseInt(tipi.determinato) || 0, colore: '#6b8cba' },
    { label: 'Stagionale', valore: parseInt(tipi.stagionale) || 0, colore: '#e07b54' },
  ].filter(c => c.valore > 0);

  const dataMese = new Date();
  dataMese.setMonth(dataMese.getMonth() + meseOffset);
  const nomeMese = dataMese.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });

  return (
    <>
      <div style={styles.grid}>
        <div style={styles.card} onClick={() => navigate('/lavoratori')}>
          <div style={styles.cardTop}>
            <h3 style={styles.cardTitle}>I tuoi Lavoratori</h3>
            {stats && <div style={styles.cardCount}><span style={styles.countNum}>{stats.lavoratori}</span></div>}
          </div>
          <p style={styles.cardDesc}>Gestisci l'anagrafica dei tuoi dipendenti</p>
        </div>
        <div style={styles.cardAccent} onClick={() => navigate('/comunicazioni')}>
          <h3 style={{ ...styles.cardTitle, color: '#232323' }}>Nuova Comunicazione</h3>
          <p style={{ ...styles.cardDesc, color: '#555' }}>Invia una comunicazione al Ministero del Lavoro</p>
        </div>
        <div style={styles.card} onClick={() => navigate('/comunicazioni')}>
          <div style={styles.cardTop}>
            <h3 style={styles.cardTitle}>Archivio</h3>
            {stats && <div style={styles.cardCount}><span style={styles.countNum}>{stats.comunicazioni}</span><span style={styles.countLabel}>quest'anno</span></div>}
          </div>
          <p style={styles.cardDesc}>Storico comunicazioni inviate</p>
        </div>
        <div style={styles.card} onClick={() => navigate('/email')}>
          <h3 style={styles.cardTitle}>Email / PEC</h3>
          <p style={styles.cardDesc}>Configura le credenziali di invio</p>
        </div>
      </div>

      {/* Alert lavoratori con contratto scaduto */}
      {statsCliente?.scaduti?.length > 0 && (
        <div style={styles.alertScaduti}>
          <div style={{ fontWeight: 600, marginBottom: '0.7rem', fontSize: '0.95rem' }}>
            ⚠️ Contratti scaduti — {statsCliente.scaduti.length} lavorator{statsCliente.scaduti.length === 1 ? 'e' : 'i'} con contratto non più valido
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, color: '#92400e' }}>Lavoratore</th>
                <th style={{ ...styles.th, color: '#92400e' }}>Data Termine</th>
              </tr>
            </thead>
            <tbody>
              {statsCliente.scaduti.map((s, i) => (
                <tr key={i} style={styles.tr}>
                  <td style={styles.td}><strong>{s.cognome}</strong> {s.nome}</td>
                  <td style={{ ...styles.td, fontWeight: 600, color: '#b91c1c' }}>{new Date(s.data_termine_contratto).toLocaleDateString('it-IT')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={styles.twoCol}>
        {/* Grafico lavoratori per tipo */}
        {statsCliente && totale > 0 && (
          <div style={styles.recentBox}>
            <h3 style={styles.recentTitle}>Lavoratori per tipologia</h3>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                {categorie.map(c => (
                  <div key={c.label} style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#555', marginBottom: '0.3rem' }}>
                      <span>{c.label}</span>
                      <strong>{c.valore}</strong>
                    </div>
                    <div style={{ background: '#f0f0f0', borderRadius: '4px', height: '10px', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.round((c.valore / totale) * 100)}%`, background: c.colore, height: '100%', borderRadius: '4px' }} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ textAlign: 'center', minWidth: '60px' }}>
                <span style={{ fontSize: '2.5rem', fontWeight: 700, color: '#232323', lineHeight: 1 }}>{totale}</span>
                <div style={{ fontSize: '0.72rem', color: '#999', marginTop: '0.2rem' }}>totale attivi</div>
              </div>
            </div>
          </div>
        )}

        {/* Scadenze fine mese */}
        <div style={styles.recentBox}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h3 style={{ ...styles.recentTitle, margin: 0 }}>
              Scadenze — {nomeMese}
              {statsCliente?.scadenze?.length > 0 && (
                <span style={{ marginLeft: '0.7rem', background: '#fef9c3', color: '#854d0e', fontSize: '0.72rem', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                  {statsCliente.scadenze.length} in scadenza
                </span>
              )}
            </h3>
            <div style={{ display: 'flex', gap: '0.3rem' }}>
              <button onClick={() => setMeseOffset(o => o - 1)} style={styles.arrowBtn}>‹</button>
              {meseOffset !== 0 && <button onClick={() => setMeseOffset(0)} style={{ ...styles.arrowBtn, fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>oggi</button>}
              <button onClick={() => setMeseOffset(o => o + 1)} style={styles.arrowBtn}>›</button>
            </div>
          </div>
          {statsCliente?.scadenze?.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Lavoratore</th>
                  <th style={styles.th}>Tipologia</th>
                  <th style={styles.th}>Scadenza</th>
                </tr>
              </thead>
              <tbody>
                {statsCliente.scadenze.map((s, i) => (
                  <tr key={i} style={styles.tr}>
                    <td style={styles.td}><strong>{s.cognome}</strong> {s.nome}</td>
                    <td style={styles.td}>{s.tipologia_contratto || s.tipo_assunzione || '—'}</td>
                    <td style={{ ...styles.td, color: '#b91c1c', fontWeight: 600 }}>{formatData(s.data_termine_contratto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ color: '#999', fontSize: '0.9rem', margin: '0.5rem 0 0' }}>Nessuna scadenza nel mese corrente.</p>
          )}
        </div>
      </div>

      {stats?.ultimeCom?.length > 0 && (
        <div style={{ ...styles.recentBox, marginTop: '1rem' }}>
          <h3 style={styles.recentTitle}>Ultime comunicazioni</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Lavoratore</th>
                <th style={styles.th}>Data Inizio</th>
                <th style={styles.th}>Data Fine</th>
                <th style={styles.th}>Stato</th>
              </tr>
            </thead>
            <tbody>
              {stats.ultimeCom.map(c => (
                <tr key={c.id} style={styles.tr}>
                  <td style={styles.td}>{c.lav_cognome} {c.lav_nome}</td>
                  <td style={styles.td}>{formatData(c.data_inizio)}</td>
                  <td style={styles.td}>{formatData(c.data_fine)}</td>
                  <td style={styles.td}>
                    <span style={{
                      background: c.stato === 'inviata' ? '#dcfce7' : c.stato === 'annullata' ? '#fef2f2' : '#fef9c3',
                      color: c.stato === 'inviata' ? '#166534' : c.stato === 'annullata' ? '#991b1b' : '#854d0e',
                      padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600,
                    }}>{c.stato.toUpperCase()}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

const styles = {
  welcome: { marginBottom: '1.5rem' },
  welcomeTitle: { margin: 0, fontSize: '1.5rem', color: '#232323', fontWeight: 600 },
  welcomeSub: { margin: '0.3rem 0 0', color: '#888', fontSize: '0.9rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  card: {
    background: 'white', padding: '1.5rem', borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer',
    border: '1px solid #eee', transition: 'box-shadow 0.15s, transform 0.15s',
  },
  cardAccent: {
    background: '#D1D179', padding: '1.5rem', borderRadius: '10px',
    cursor: 'pointer', border: 'none', transition: 'transform 0.15s',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  cardTitle: { margin: 0, fontSize: '1rem', color: '#232323', fontWeight: 600 },
  cardDesc: { margin: '0.5rem 0 0', color: '#999', fontSize: '0.85rem' },
  cardCount: { textAlign: 'right' },
  countNum: { fontSize: '1.8rem', fontWeight: 700, color: '#232323', lineHeight: 1 },
  countLabel: { display: 'block', fontSize: '0.7rem', color: '#999' },
  alertBox: {
    background: '#fef9c3', border: '1px solid #fde68a', padding: '1rem 1.2rem',
    borderRadius: '8px', fontSize: '0.9rem', color: '#854d0e',
    display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
  },
  alertLink: {
    background: 'none', border: 'none', color: '#854d0e', fontWeight: 600,
    cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem', marginLeft: 'auto',
  },
  alertScaduti: {
    background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '10px',
    padding: '1.2rem 1.5rem', marginBottom: '1rem', color: '#92400e',
  },
  twoCol: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: '1rem', marginBottom: '1rem',
  },
  recentBox: {
    background: 'white', padding: '1.5rem', borderRadius: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #eee',
  },
  recentTitle: { margin: '0 0 1rem', fontSize: '1rem', color: '#232323' },
  arrowBtn: { background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '5px', cursor: 'pointer', fontSize: '1rem', padding: '0.2rem 0.6rem', color: '#555', lineHeight: 1 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.6rem 0.8rem', borderBottom: '2px solid #f0f0f0', fontSize: '0.8rem', color: '#888', fontWeight: 600 },
  tr: { borderBottom: '1px solid #f5f5f5' },
  td: { padding: '0.6rem 0.8rem', fontSize: '0.85rem', color: '#333' },
};
